/**
 * The tag byte that says what a frame carries.
 *
 * One table, numeric, because a string kind on a 20 Hz snapshot is bytes spent saying the same
 * word over and over. Numbers are assigned once and never reused: a retired kind leaves its number
 * behind rather than handing it to something else, since an old build on the other end would then
 * read the new message as the old one.
 *
 * A kind is not a dispatch table. Whoever receives a frame decides what to do with it; nothing here
 * knows what a snapshot means.
 */

export const MESSAGE_KINDS = {
  /** Version and role claim, exchanged before a peer is accepted. */
  hello: 1,
  /** Lobby membership and chat-shaped events. Reliable and ordered. */
  lobby: 2,
  /** Authoritative state from the host. Unreliable and unordered. */
  snapshot: 3,
  /** Input from a guest to the host. Unreliable and unordered. */
  input: 4,
  /** Liveness probe, answered with `pong`, carrying the sender's own stamp to hand back. */
  ping: 5,
  /** That stamp, returned untouched. See [peerHeartbeat](../session/peerHeartbeat.ts). */
  pong: 6,
  /** The host telling a guest which mode to open, on what seed, and which player it is. */
  sessionStart: 7,
  /**
   * What the host declares happened this tick, to the one player it happened to.
   *
   * Reliable and ordered, unlike a snapshot: a lost world is a frame of staleness, a lost hit
   * marker is feedback that never arrives. See [eventFrame](../sync/frames/eventFrame.ts).
   */
  simEvent: 8,
  /**
   * The place this session is being played in: a seed, an archetype and a depth.
   *
   * The recipe alone, never a zone. Every peer generates locally from it, which is the whole
   * arrangement: see [zoneRecipeFrame](./zoneRecipeFrame.ts).
   */
  zoneRecipe: 9,
  /**
   * What one peer's generated zone hashed to, exchanged once at load so a divergence is loud.
   *
   * See [zoneHashFrame](./zoneHashFrame.ts) and `sim/worldgen/zoneHash.ts`.
   */
  zoneHash: 10,
  /**
   * A peer saying it has walked into the raid and does not know where the raid is.
   *
   * Empty: the whole message is that somebody is now listening. It exists because a mode arrives
   * on a chunk the browser fetches, so the host has already announced the place by the time a
   * guest's raid is running, and a peer that was told nothing has no way to be told again. See
   * [raidZoneLink](../../modes/raid/net/raidZoneLink.ts).
   */
  zoneAsk: 11,
} as const;

export type MessageKindName = keyof typeof MESSAGE_KINDS;

export type MessageKind = (typeof MESSAGE_KINDS)[MessageKindName];

const KNOWN_KINDS: ReadonlySet<number> = new Set(Object.values(MESSAGE_KINDS));

/** Guards the tag byte off the wire. An unknown kind is a malformed frame, not a default case. */
export function isMessageKind(value: number): value is MessageKind {
  return KNOWN_KINDS.has(value);
}

/** The name for a tag, for logs and the diagnostics panel. */
export function messageKindName(kind: MessageKind): MessageKindName {
  const found = (Object.keys(MESSAGE_KINDS) as MessageKindName[]).find(
    (name) => MESSAGE_KINDS[name] === kind,
  );
  return found ?? 'hello';
}
